import { useEffect } from 'react';

export type UiComponentKind = 'page' | 'modal' | 'overlay' | 'component';

export interface UiComponentSnapshotItem {
  name: string;
  kind: UiComponentKind;
  count: number;
}

const registry = new Map<string, UiComponentSnapshotItem>();
const kindOrder: UiComponentKind[] = ['page', 'modal', 'overlay', 'component'];

const normalizeName = (name: string) => name.trim();
const makeKey = (name: string, kind: UiComponentKind) => `${kind}:${name}`;

export function registerUiComponent(name: string, kind: UiComponentKind = 'component') {
  const normalizedName = normalizeName(name);
  if (!normalizedName) return;
  const key = makeKey(normalizedName, kind);
  const existing = registry.get(key);
  if (existing) {
    existing.count += 1;
    return;
  }
  registry.set(key, { name: normalizedName, kind, count: 1 });
}

export function unregisterUiComponent(name: string, kind: UiComponentKind = 'component') {
  const normalizedName = normalizeName(name);
  if (!normalizedName) return;
  const key = makeKey(normalizedName, kind);
  const existing = registry.get(key);
  if (!existing) return;
  existing.count -= 1;
  if (existing.count <= 0) {
    registry.delete(key);
  }
}

export function getUiSnapshot(): UiComponentSnapshotItem[] {
  return Array.from(registry.values())
    .filter((entry) => entry.count > 0)
    .map((entry) => ({ ...entry }))
    .sort((a, b) => {
      const kindDiff = kindOrder.indexOf(a.kind) - kindOrder.indexOf(b.kind);
      if (kindDiff !== 0) return kindDiff;
      return a.name.localeCompare(b.name);
    });
}

export function useUiComponent(name: string | null | undefined, kind: UiComponentKind = 'component') {
  useEffect(() => {
    if (!name) return;
    registerUiComponent(name, kind);
    return () => unregisterUiComponent(name, kind);
  }, [name, kind]);
}
